import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdArrowBack, MdAccessTime, MdPeople, MdCheckCircle, MdCancel, MdInfo, MdBarChart, MdPerson, MdSchool, MdCake, MdPrint, MdEmail } from 'react-icons/md';
import { formatVoterCount } from '../utils/formatVoterCount';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';

function CandidacyReportPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('tags');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');

  const fetchReport = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/candidacy/reports/${reportId}`);
      setReport(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    fetchReport();
    const interval = setInterval(() => {
      if (report && (report.status === 'pending' || report.status === 'processing')) {
        fetchReport();
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [reportId, fetchReport, report]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
    });
  };

  const getLevelLabel = (level) => {
    const labels = { local: 'Local', state: 'State', national: 'National' };
    return labels[level] || level;
  };

  const handlePrint = () => {
    // Temporarily expand all sections for printing
    window.print();
  };

  const handleEmail = async () => {
    setEmailSending(true);
    setEmailMessage('');
    try {
      await axios.post(`${API_URL}/api/candidacy/reports/${reportId}/email`);
      setEmailMessage('Report sent to your email!');
      setTimeout(() => setEmailMessage(''), 5000);
    } catch (err) {
      setEmailMessage(err.response?.data?.detail || 'Failed to send email');
      setTimeout(() => setEmailMessage(''), 5000);
    } finally {
      setEmailSending(false);
    }
  };

  const getMatchColor = (pct) => {
    if (pct >= 70) return 'text-green-600';
    if (pct >= 50) return 'text-amber-600';
    return 'text-red-500';
  };

  const getMatchBg = (pct) => {
    if (pct >= 70) return 'bg-green-500';
    if (pct >= 50) return 'bg-amber-500';
    return 'bg-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 pt-8 py-6">
        <button onClick={() => navigate('/transactions')} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors mb-4 font-rubik text-sm font-medium shadow-sm" data-testid="back-to-transactions">
          <MdArrowBack /> Back to Transactions
        </button>
        <div className="bg-red-50 border border-red-200 rounded-card p-6 text-red-700 font-rubik">{error}</div>
      </div>
    );
  }

  if (!report) return null;

  const results = report.results;
  const isPending = report.status === 'pending' || report.status === 'processing';
  const isFailed = report.status === 'failed';

  return (
    <div className="max-w-4xl mx-auto px-4 pt-8 py-6" data-testid="candidacy-report-page">
      {/* Back button */}
      <button
        onClick={() => navigate('/transactions')}
        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors mb-6 font-rubik text-sm font-medium shadow-sm"
        data-testid="back-to-transactions"
      >
        <MdArrowBack className="text-lg" /> Back to Transactions
      </button>

      {/* Header */}
      <div className="bg-white rounded-card shadow-card border p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 font-rubik" data-testid="report-title">
              {getLevelLabel(report.level)} Candidacy Assessment
            </h1>
            <p className="text-sm text-gray-500 font-rubik mt-1">
              Report generated on {formatDate(report.createdAt)}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {report.status === 'completed' && (
              <>
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors print:hidden"
                  data-testid="print-report-btn"
                >
                  <MdPrint className="text-lg" /> Print
                </button>
                <button
                  onClick={handleEmail}
                  disabled={emailSending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50 print:hidden"
                  data-testid="email-report-btn"
                >
                  <MdEmail className="text-lg" /> {emailSending ? 'Sending...' : 'Email'}
                </button>
              </>
            )}
            <div data-testid="report-status-badge">
            {isPending && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
                Processing...
              </span>
            )}
            {report.status === 'completed' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700">
                <MdCheckCircle /> Completed
              </span>
            )}
            {isFailed && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 text-red-700">
                <MdCancel /> Failed
              </span>
            )}
          </div>
          </div>
        </div>
        {emailMessage && (
          <div className={`mt-3 text-sm font-rubik px-3 py-2 rounded-lg print:hidden ${emailMessage.includes('Failed') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-testid="email-message">
            {emailMessage}
          </div>
        )}
      </div>

      {/* Pending state */}
      {isPending && (
        <div className="bg-white rounded-card shadow-card border p-8 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-700 font-rubik mb-2">Generating Your Report</h3>
          <p className="text-gray-500 font-rubik">Analyzing poll responses in your area. This may take a moment...</p>
        </div>
      )}

      {/* Failed state */}
      {isFailed && (
        <div className="bg-red-50 border border-red-200 rounded-card p-6 text-center">
          <MdCancel className="text-4xl text-red-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-700 font-rubik mb-2">Report Generation Failed</h3>
          <p className="text-red-600 font-rubik text-sm">{report.errorMessage || 'An unexpected error occurred.'}</p>
        </div>
      )}

      {/* Completed results */}
      {report.status === 'completed' && results && (
        <>
          {/* Snapshot disclaimer */}
          <div className="bg-blue-50 border border-blue-200 rounded-card p-4 mb-6 flex items-start gap-3" data-testid="snapshot-disclaimer">
            <MdInfo className="text-blue-500 text-xl flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-rubik font-medium">Point-in-Time Snapshot</p>
              <p className="text-xs text-blue-600 font-rubik mt-0.5">
                This report reflects user responses as of {formatDate(report.snapshotTimestamp)}. Results are subject to change as more users participate in polls.
              </p>
            </div>
          </div>

          {/* Overall Score Card */}
          <div className="bg-white rounded-card shadow-card border p-6 mb-6" data-testid="overall-score-card">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="md:col-span-1 flex flex-col items-center justify-center">
                <div className={`text-5xl font-bold font-rubik ${getMatchColor(results.overall_match_pct)}`}>
                  {results.overall_match_pct}%
                </div>
                <p className="text-sm text-gray-500 font-rubik mt-1">Overall Match</p>
              </div>
              <div className="md:col-span-3 grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <MdBarChart className="text-2xl text-primary mx-auto mb-1" />
                  <p className="text-xl font-bold text-gray-800 font-rubik">{results.total_tags_analyzed || 0}</p>
                  <p className="text-xs text-gray-500 font-rubik">Tags Analyzed</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <MdAccessTime className="text-2xl text-secondary mx-auto mb-1" />
                  <p className="text-xl font-bold text-gray-800 font-rubik">{results.total_polls_analyzed || 0}</p>
                  <p className="text-xs text-gray-500 font-rubik">Polls Compared</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <MdPeople className="text-2xl text-tertiary mx-auto mb-1" />
                  <p className="text-xl font-bold text-gray-800 font-rubik">{formatVoterCount(results.total_comparison_users || 0)}</p>
                  <p className="text-xs text-gray-500 font-rubik">Voters Compared</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-white/10 rounded-lg p-1 mb-6" data-testid="report-tabs">
            <button
              onClick={() => setActiveTab('tags')}
              className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium font-rubik transition-colors ${activeTab === 'tags' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-300 hover:text-white'}`}
              data-testid="tab-tags"
            >
              By Topic
            </button>
            <button
              onClick={() => setActiveTab('questions')}
              className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium font-rubik transition-colors ${activeTab === 'questions' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-300 hover:text-white'}`}
              data-testid="tab-questions"
            >
              By Question
            </button>
            <button
              onClick={() => setActiveTab('demographics')}
              className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium font-rubik transition-colors ${activeTab === 'demographics' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-300 hover:text-white'}`}
              data-testid="tab-demographics"
            >
              Demographics
            </button>
          </div>

          {/* Tags Tab - Hierarchical */}
          {activeTab === 'tags' && (
            <>
              {results.tag_results && results.tag_results.length > 0 ? (
                <div className="space-y-4" data-testid="tag-results">
                  <div className="bg-white/10 rounded-card p-4 border border-white/10" data-testid="tag-results-explainer">
                    <p className="text-sm text-gray-300 font-rubik leading-relaxed">
                      <span className="text-white font-medium">Match %</span> — The percentage of voters in your area who answered the same way you did on polls within that topic. A higher percentage means stronger alignment with local opinion.
                      <span className="text-white font-medium ml-2">Majority</span> — How often your answers matched the most popular response.
                    </p>
                  </div>
                  {results.tag_results.map((tag) => (
                    <HierarchicalTagCard key={tag.tag_id} tag={tag} getMatchColor={getMatchColor} getMatchBg={getMatchBg} />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-card shadow-card border p-8 text-center">
                  <p className="text-gray-500 font-rubik">{results.summary || 'No tag data available for this report.'}</p>
                </div>
              )}
            </>
          )}

          {/* Questions Tab - Flat list */}
          {activeTab === 'questions' && (
            <>
              {results.question_results && results.question_results.length > 0 ? (
                <div className="space-y-2" data-testid="question-results">
                  <div className="bg-white/10 rounded-card p-4 border border-white/10 mb-4">
                    <p className="text-sm text-gray-300 font-rubik leading-relaxed">
                      <span className="text-white font-medium">All Questions</span> — Every poll question you answered, sorted by match percentage with voters in your area.
                    </p>
                  </div>
                  {results.question_results.map((q) => (
                    <QuestionResultRow key={q.poll_id} question={q} getMatchColor={getMatchColor} getMatchBg={getMatchBg} />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-card shadow-card border p-8 text-center">
                  <p className="text-gray-500 font-rubik">No question data available for this report.</p>
                </div>
              )}
            </>
          )}

          {/* Demographics Tab */}
          {activeTab === 'demographics' && (
            <>
              <div className="bg-white/10 rounded-card p-4 border border-white/10 mb-4" data-testid="demographics-explainer">
                <p className="text-sm text-gray-300 font-rubik leading-relaxed">
                  <span className="text-white font-medium">Demographic Breakdown</span> — Shows how well your poll responses align with different voter groups. Expand each group to see a per-topic breakdown.
                </p>
              </div>

              {/* Radar Chart Summary */}
              {results.demographics && results.demographics.length > 0 && (
                <DemographicRadarChart demographics={results.demographics} />
              )}
              {results.demographics && results.demographics.length > 0 ? (
                <div className="space-y-6" data-testid="demographics-results">
                  {results.demographics.map((cat) => (
                    <DemographicCategory key={cat.category_key} category={cat} getMatchColor={getMatchColor} getMatchBg={getMatchBg} />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-card shadow-card border p-8 text-center">
                  <p className="text-gray-500 font-rubik">No demographic data available for this report.</p>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function HierarchicalTagCard({ tag, getMatchColor, getMatchBg }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedSubtag, setExpandedSubtag] = useState(null);
  const hasSubtags = tag.subtags && tag.subtags.length > 0;

  return (
    <div className="bg-white rounded-card shadow-card border overflow-hidden" data-testid={`tag-result-${tag.tag_id}`}>
      {/* Parent tag header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        data-testid={`tag-expand-${tag.tag_id}`}
      >
        <div className="flex items-center gap-3">
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: tag.tag_color }}
          ></span>
          <span className="font-medium text-gray-800 font-rubik">{tag.tag_name}</span>
          {hasSubtags && (
            <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5 font-rubik">
              {tag.subtags.length} subtopic{tag.subtags.length !== 1 ? 's' : ''}
            </span>
          )}
          <span className="text-xs text-gray-400 font-rubik">({tag.polls_analyzed} poll{tag.polls_analyzed !== 1 ? 's' : ''})</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-24 bg-gray-200 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full ${getMatchBg(tag.match_pct)}`}
                style={{ width: `${Math.min(tag.match_pct, 100)}%` }}
              ></div>
            </div>
            <span className={`text-sm font-bold font-rubik min-w-[48px] text-right ${getMatchColor(tag.match_pct)}`}>
              {tag.match_pct}%
            </span>
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            {tag.majority_alignment >= 50 ? (
              <MdCheckCircle className="text-green-500" />
            ) : (
              <MdCancel className="text-red-400" />
            )}
            <span className="font-rubik hidden sm:inline">{tag.majority_alignment}% majority</span>
          </div>
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t">
          {/* Subtag breakdown (if hierarchical) */}
          {hasSubtags ? (
            <div className="divide-y divide-gray-100">
              {tag.subtags.map((subtag) => (
                <div key={subtag.tag_id}>
                  {/* Subtag row */}
                  <button
                    onClick={() => setExpandedSubtag(expandedSubtag === subtag.tag_id ? null : subtag.tag_id)}
                    className="w-full flex items-center justify-between px-6 py-3 hover:bg-gray-50 transition-colors"
                    data-testid={`subtag-expand-${subtag.tag_id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: subtag.tag_color }}></span>
                      <span className="text-sm text-gray-700 font-rubik font-medium">{subtag.tag_name}</span>
                      <span className="text-xs text-gray-400 font-rubik">({subtag.polls_analyzed} poll{subtag.polls_analyzed !== 1 ? 's' : ''})</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${getMatchBg(subtag.match_pct)}`}
                            style={{ width: `${Math.min(subtag.match_pct, 100)}%` }}
                          ></div>
                        </div>
                        <span className={`text-xs font-bold font-rubik min-w-[40px] text-right ${getMatchColor(subtag.match_pct)}`}>
                          {subtag.match_pct}%
                        </span>
                      </div>
                      <svg className={`w-3 h-3 text-gray-400 transition-transform ${expandedSubtag === subtag.tag_id ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Subtag's poll details */}
                  {expandedSubtag === subtag.tag_id && subtag.poll_details && (
                    <div className="bg-gray-50 divide-y divide-gray-100">
                      {subtag.poll_details.map((poll) => (
                        <PollDetailRow key={poll.poll_id} poll={poll} getMatchColor={getMatchColor} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* No subtags - show poll details directly */
            tag.poll_details && (
              <div className="bg-gray-50 divide-y divide-gray-100">
                {tag.poll_details.map((poll) => (
                  <PollDetailRow key={poll.poll_id} poll={poll} getMatchColor={getMatchColor} />
                ))}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

function PollDetailRow({ poll, getMatchColor }) {
  return (
    <div className="px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`poll-detail-${poll.poll_id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700 font-rubik truncate">{poll.question}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${poll.user_answer === 'yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            You: {poll.user_answer.toUpperCase()}
          </span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${poll.majority_answer === 'yes' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
            Majority: {poll.majority_answer.toUpperCase()}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`text-sm font-bold font-rubik ${getMatchColor(poll.agree_pct)}`}>
          {poll.agree_pct}%
        </span>
        {poll.matches_majority ? (
          <MdCheckCircle className="text-green-500 text-lg" title="Matches majority" />
        ) : (
          <MdCancel className="text-red-400 text-lg" title="Against majority" />
        )}
      </div>
    </div>
  );
}

function QuestionResultRow({ question, getMatchColor, getMatchBg }) {
  return (
    <div className="bg-white rounded-card shadow-card border p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`question-result-${question.poll_id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 font-rubik font-medium">{question.question}</p>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${question.user_answer === 'yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            You: {question.user_answer.toUpperCase()}
          </span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${question.majority_answer === 'yes' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
            Majority: {question.majority_answer.toUpperCase()}
          </span>
          {question.tag_name && (
            <span 
              className="text-xs font-medium px-2 py-0.5 rounded-full text-white"
              style={{ backgroundColor: question.tag_color || '#6b7280' }}
            >
              {question.tag_name}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-20 bg-gray-200 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full ${getMatchBg(question.agree_pct)}`}
              style={{ width: `${Math.min(question.agree_pct, 100)}%` }}
            ></div>
          </div>
          <span className={`text-sm font-bold font-rubik min-w-[44px] text-right ${getMatchColor(question.agree_pct)}`}>
            {question.agree_pct}%
          </span>
        </div>
        {question.matches_majority ? (
          <MdCheckCircle className="text-green-500 text-lg" title="Matches majority" />
        ) : (
          <MdCancel className="text-red-400 text-lg" title="Against majority" />
        )}
      </div>
    </div>
  );
}

const CHART_COLORS = ['#08B23C', '#3DCED3', '#018F83', '#6366F1'];

function DemographicRadarChart({ demographics }) {
  // Build radar data: one axis per demographic group across all categories
  // Show the top groups from each category
  const radarData = [];
  demographics.forEach((cat) => {
    cat.groups.forEach((group) => {
      radarData.push({
        name: group.group_name,
        match: group.overall_match_pct,
        category: cat.category,
        voters: group.user_count,
      });
    });
  });

  // Sort by match descending and take top 12 for readability
  radarData.sort((a, b) => b.match - a.match);
  const chartData = radarData.slice(0, 12);

  // Also build a summary bar view per category (avg match per category)
  const categorySummary = demographics.map((cat) => {
    const groups = cat.groups;
    const avgMatch = groups.length > 0
      ? Math.round(groups.reduce((s, g) => s + g.overall_match_pct, 0) / groups.length * 10) / 10
      : 0;
    const bestGroup = groups.length > 0 ? groups[0] : null;
    const worstGroup = groups.length > 1 ? groups[groups.length - 1] : null;
    return { category: cat.category, key: cat.category_key, avgMatch, bestGroup, worstGroup, totalGroups: groups.length };
  });

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-white shadow-lg rounded-lg px-3 py-2 border text-xs font-rubik">
          <p className="font-semibold text-gray-800">{d.name}</p>
          <p className="text-gray-500">{d.category}</p>
          <p className="text-primary font-bold">{d.match}% match</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-card shadow-card border p-6 mb-6" data-testid="demographics-radar-chart">
      <h3 className="text-base font-semibold text-gray-800 font-rubik mb-4">Alignment Overview</h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <div className="flex items-center justify-center">
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="70%">
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis
                dataKey="name"
                tick={{ fontSize: 10, fill: '#6b7280', fontFamily: 'Rubik' }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                tick={{ fontSize: 9, fill: '#9ca3af' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Radar
                name="Match %"
                dataKey="match"
                stroke="#08B23C"
                fill="#08B23C"
                fillOpacity={0.2}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Category Summary Cards */}
        <div className="space-y-3">
          {categorySummary.map((cat, idx) => (
            <div key={cat.key} className="bg-gray-50 rounded-lg p-3" data-testid={`demo-summary-${cat.key}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-gray-800 font-rubik">{cat.category}</span>
                <span className="text-sm font-bold font-rubik" style={{ color: CHART_COLORS[idx % CHART_COLORS.length] }}>
                  {cat.avgMatch}% avg
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(cat.avgMatch, 100)}%`, backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500 font-rubik">
                {cat.bestGroup && (
                  <span>Strongest: <span className="text-green-600 font-medium">{cat.bestGroup.group_name} ({cat.bestGroup.overall_match_pct}%)</span></span>
                )}
                {cat.worstGroup && cat.worstGroup.group_name !== cat.bestGroup?.group_name && (
                  <span>Weakest: <span className="text-red-500 font-medium">{cat.worstGroup.group_name} ({cat.worstGroup.overall_match_pct}%)</span></span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


const CATEGORY_ICONS = {
  gender: MdPerson,
  ethnicity: MdPeople,
  education: MdSchool,
  age_group: MdCake,
};

function DemographicCategory({ category, getMatchColor, getMatchBg }) {
  const Icon = CATEGORY_ICONS[category.category_key] || MdPeople;

  return (
    <div data-testid={`demo-category-${category.category_key}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="text-xl text-white" />
        <h3 className="text-base font-semibold text-white font-rubik">{category.category}</h3>
      </div>
      <div className="space-y-3">
        {category.groups.map((group) => (
          <DemographicGroupCard key={group.group_name} group={group} getMatchColor={getMatchColor} getMatchBg={getMatchBg} />
        ))}
      </div>
    </div>
  );
}

function DemographicGroupCard({ group, getMatchColor, getMatchBg }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-card shadow-card border overflow-hidden" data-testid={`demo-group-${group.group_name}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        data-testid={`demo-group-expand-${group.group_name}`}
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-gray-800 font-rubik">{group.group_name}</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-24 bg-gray-200 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full ${getMatchBg(group.overall_match_pct)}`}
                style={{ width: `${Math.min(group.overall_match_pct, 100)}%` }}
              ></div>
            </div>
            <span className={`text-sm font-bold font-rubik min-w-[48px] text-right ${getMatchColor(group.overall_match_pct)}`}>
              {group.overall_match_pct}%
            </span>
          </div>
          <span className="text-xs text-gray-400 font-rubik hidden sm:inline">{group.tags_analyzed} tag{group.tags_analyzed !== 1 ? 's' : ''}</span>
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && group.tag_breakdowns && (
        <div className="border-t bg-gray-50 divide-y divide-gray-100 max-h-72 overflow-y-auto">
          {group.tag_breakdowns.map((tag) => (
            <div key={tag.tag_id} className="px-4 py-2.5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.tag_color }}
                ></span>
                <span className="text-sm text-gray-700 font-rubik truncate">{tag.tag_name}</span>
                <span className="text-xs text-gray-400 font-rubik flex-shrink-0">({tag.polls_analyzed})</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="w-16 bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${getMatchBg(tag.match_pct)}`}
                    style={{ width: `${Math.min(tag.match_pct, 100)}%` }}
                  ></div>
                </div>
                <span className={`text-xs font-bold font-rubik min-w-[36px] text-right ${getMatchColor(tag.match_pct)}`}>
                  {tag.match_pct}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CandidacyReportPage;
